/// <amd-module name="Release/Landing/LeftPanel/AllPipelines/SearchDefinitionsTreeProvider" />
import { TreeItemProvider as TreeItemExProvider, ITreeItem as ITreeItem2 } from "VSSUI/Utilities/TreeItemProvider";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { ObservableValue } from "VSS/Core/Observable";

import { Folder, ReleaseDefinition } from "Release/Client/Contracts/Release";
import { INode, NodeType, ISearchNode } from "./Types";
import { FolderUtils } from "./FolderUtils";

function _getTreeItemMap(folders: Folder[], definitions: ReleaseDefinition[]): { [id: string]: ISearchNode } {
    let treeItems: { [id: string]: ISearchNode };

    const root: ISearchNode = {
        item: _createRootFolder(),
        children: [],
    };

    treeItems = {
        "\\": root
    };

    /*
        \
        \a\b
        \a\b\c
        \a\d
        \e
        \e\f
        \f\g
    */
    folders.sort((a: Folder, b: Folder) => localeIgnoreCaseComparer(a.path, b.path)).forEach((folder) => {
        // all paths start with \
        const path = folder.path.substring(1);
        if (path.length > 0) {
            // create this item
            const segments: string[] = path.split("\\");
            const name = segments[segments.length - 1];
            const treeNode: ISearchNode = {
                item: FolderUtils.convertFolder(folder, true),
                children: []
            };
            treeItems[folder.path] = treeNode;

            const parent: ISearchNode = _buildFoldersTree(
                segments.slice(0, segments.length - 1),
                treeItems
            );
            parent.children.push(treeNode.item);
        }
        else {
            // the root
            root.item.folder = folder;
        }
    });

    definitions.sort((a: ReleaseDefinition, b: ReleaseDefinition) => localeIgnoreCaseComparer(a.name, b.name)).forEach((definition) => {
        const treeItem: INode = FolderUtils.convertDefinition(definition);

        treeItems[definition.id.toString()] = {
            item: treeItem,
            children: []
        };

        treeItems[definition.path].children.push(treeItem);
    });

    return treeItems;
}

function _createRootFolder(): INode {
    let rootFolder = {
        path: FolderUtils.RootFolderPath
    } as Folder;

    return {
        type: NodeType.Folder,
        folder: rootFolder,
        releaseDefinition: {},
        isExpandable: true,
        defaultIsExpanded: true
    } as INode;
}

function _buildFoldersTree(pathSegments: string[], treeItems: { [id: string]: ISearchNode }): ISearchNode {
    if (pathSegments.length === 0) {
        return treeItems["\\"];
    }

    const parentPath = "\\" + pathSegments.join("\\");
    let parent: ISearchNode = treeItems[parentPath];
    if (!parent) {
        const grandparent = _buildFoldersTree(pathSegments.slice(0, pathSegments.length - 1), treeItems);
        let parentFolder: Folder = { path: parentPath } as Folder;
        parent = {
            item: FolderUtils.convertFolder(parentFolder, true),
            children: []
        };

        treeItems[parentPath] = parent;

        grandparent.children.push(parent.item);
    }
    return parent;
}

type ObservableNode = ObservableValue<INode>;
type TreeExNode = ITreeItem2<ObservableNode>;


function _getTreeExItemFromNode(node: INode): TreeExNode {
    return {
        data: new ObservableValue(node),
        childItems: [],
        expanded: true
    } as TreeExNode;
}

export class SearchDefinitionsTreeExProvider extends TreeItemExProvider<ObservableNode> {
    constructor(folders: Folder[], definitions: ReleaseDefinition[]) {
        super([]);

        this._treeItemMap = _getTreeItemMap(folders, definitions);
        const rootItem = _getTreeExItemFromNode(this._treeItemMap["\\"].item);
        this.add(rootItem);

        this._addChildItemsRecursively(rootItem);
    }

    private _addChildItemsRecursively(item: TreeExNode): void {
        const data = item.data.value;
        if (!FolderUtils.isFolder(data)) {
            return;
        }

        // We have to reverse the list of child nodes because the nodes are inserted right after the parent. So as we go through the array, if the nodes are sorted alphabetically ascending
        // The last one will be inserted after the parent, and the first one will be pushed to the end. Hence reversing, to have reverse alphabetical order in this, and finally correct alph order
        const childNodes = this._treeItemMap[data.folder.path].children.reverse();
        childNodes.forEach(cn => {
            const childItem = _getTreeExItemFromNode(cn);
            this.splice(item, undefined, [{ items: [childItem] }]);
            this._addChildItemsRecursively(childItem);
        });
    }

    private _treeItemMap: { [id: string]: ISearchNode };
}